// Angular modules.
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { CalendarAvailabilitiesService } from 'app/calendar/services/calendar-availabilities.service';
import { CalendarUtilsService } from 'app/calendar/services/calendar-utils.service';
import { RecurrencyAttemptService } from 'app/calendar/services/recurrency-attempt.service';
// Zeklub enums.
import { IBusySlot } from 'app/interfaces/availability.interface';
// Zenklub interfaces.
import { IDay } from 'app/interfaces/calendar.interface';
import { ICalendarEvent, IDayEvents } from 'app/interfaces/event.interface';
import { IRecurrencyAttempt } from 'app/interfaces/recurrent-attempts.interface';
import { ISidebar } from 'app/interfaces/sidebar.interface';
// Zenklub services.
import { AvailabilitiesService } from 'app/services/availabilities.service';
import { DateTimeService } from 'app/services/datetime.service';
import { EventsService } from 'app/services/events.service';
import { GlobalService } from 'app/services/global.service';
import { tableAnimation } from 'app/utils/animations.util';
import { DateTime, Interval } from 'luxon';
import { Subscription, forkJoin } from 'rxjs';

@Component({
	selector: 'app-daycalendar',
	templateUrl: './daycalendar.component.html',
	styleUrls: ['./daycalendar.component.scss'],
	animations: [tableAnimation],
})
export class DaycalendarComponent implements OnChanges, OnDestroy, OnInit {
	// Inputs.
	@Input() activeDate: IDay;
	@Input() firstCall: boolean;
	@Input() forceRefreshEvents: boolean;
	@Input() avaibilities;
	// Outputs.
	@Output() showSelectedEvent: EventEmitter<ISidebar> =
		new EventEmitter<ISidebar>();
	@Output() showDayEvents: EventEmitter<ISidebar> =
		new EventEmitter<ISidebar>();
	@Output() confirmRefreshEvents: EventEmitter<boolean> =
		new EventEmitter<boolean>();
	cellProportion = 25 / 30;
	// Hours.
	hours: IDayEvents[] = [];
	hourIntervalNow: string;
	avaibilitiesForDay = [];
	// Hold events.
	eventsList: ICalendarEvent[] = [];
	eventsListed = false;
	recurrencyAttempts: IRecurrencyAttempt[] = [];

	// Status.
	currentDayVisible = false;
	isLoading = false;
	userRequestedReschedule = false;
	lastEventsCheck: Date;

	// Calendar data.
	activeShowDayName: string;
	lastDay: Date;

	// Total events of current day.
	dayTotalEvents = 0;

	// Listener for new events.
	newEvSubscription: Subscription;
	// Constructor method.
	constructor(
		private availabilityService: AvailabilitiesService,
		private dateTime: DateTimeService,
		private events: EventsService,
		private global: GlobalService,
		private recurrencyAttemptService: RecurrencyAttemptService,
		public calendarAvailabilitiesService: CalendarAvailabilitiesService,
		public calendarUtils: CalendarUtilsService
	) {}

	ngOnInit() {
		if (this.firstCall) {
			this.firstCall = false;
			this.calendarUtils.showEventsOfDay(new Date(), this.showDayEvents);
		}

		if (!this.eventsListed) {
			this.getEvents(true);
		}
		this.events.eventEmitter().subscribe((event) => this.setupSlots(event));
	}

	ngOnChanges() {
		if (this.activeDate.date !== this.lastDay) {
			this.lastDay = this.activeDate.date;
			this.eventsListed = false;
		}

		// Get events from period.
		if (!this.eventsListed || this.forceRefreshEvents) {
			// Update events list.
			this.getEvents(true);
			// Updated events.
			if (this.forceRefreshEvents) {
				this.forceRefreshEvents = false;
				this.confirmRefreshEvents.emit(false);
			}
		}

		// Get current interval.
		this.hourIntervalNow = this.dateTime.getHourInterval();
		// Current day is visible?
		this.isCurrentDayVisible();
		this.avaibilitiesForDay =
			this.calendarAvailabilitiesService.getAvaibilitiesForPeriod(
				this.avaibilities,
				DateTime.fromJSDate(this.activeDate.date).set({ hour: 0, minute: 0 }),
				DateTime.fromJSDate(this.activeDate.date).set({ hour: 23, minute: 59 })
			);
	}

	// On destroy.
	public ngOnDestroy(): void {
		// Unsubscribe to new events alerts.
		if (this.newEvSubscription) {
			this.newEvSubscription.unsubscribe();
		}
	}

	// Verifiy if current day is visible.
	private isCurrentDayVisible(): void {
		const curDate: Date = this.activeDate.date;
		const now: Date = new Date();
		curDate.setHours(0, 0, 0, 0);
		now.setHours(0, 0, 0, 0);
		this.currentDayVisible = curDate.toString() === now.toString();
	}

	// Get events.
	private getEvents(showLoader = true): void {
		// Events listed.
		this.eventsListed = true;

		// Loading.
		if (showLoader) {
			this.isLoading = true;
		}

		// Hours list.
		this.hours = [];
		for (let i = 0; i < 48; i++) {
			const startLuxonDate = DateTime.fromJSDate(this.activeDate.date).set({
				hour: Math.floor(i / 2),
				minute: i % 2 !== 0 ? 30 : 0,
			});
			const endLuxonSlotDate = startLuxonDate.plus({ minutes: 30 });
			const hour = this.dateTime.getFormatedHoursAlt(
				Math.floor(i / 2),
				i % 2 !== 0 ? 30 : undefined
			);
			this.hours.push({
				isHalfHour: i % 2 !== 0,
				hour,
				events: [],
				busySlots: [],
				recurrencyAttempts: [],
				luxonDate: startLuxonDate,
				hasRecurrencyOverlaping: false,
				interval: Interval.fromDateTimes(startLuxonDate, endLuxonSlotDate),
			});
		}

		// Get current day.
		const firstDay = this.activeDate.date;

		// Get events.
		this.eventsList = [];

		forkJoin([
			this.events.getEventsForDay(firstDay),
			this.recurrencyAttemptService.getRecurrenciesAttempts({
				startDate: DateTime.fromJSDate(firstDay, { zone: 'utc' })
					.set({ hour: 0, minute: 0 })
					.toISO(),
				endDate: DateTime.fromJSDate(firstDay, { zone: 'utc' })
					.set({ hour: 23, minute: 59 })
					.toISO(),
			}),
		]).subscribe(([events, recurrencyAttempts]) => {
			console.log('events', events);
			this.eventsList = events;
			// Store total events.
			this.dayTotalEvents = 0;

			this.getBusySlots();
			this.setupSlots();

			// Loaded.
			this.isLoading = false;

			this.calendarUtils.scrollToHour();

			this.recurrencyAttempts =
				this.recurrencyAttemptService.getFormattedRecurrencyAttempts(
					recurrencyAttempts?.data
				);
			this.setupRecurrencyAttempts();
		});
	}

	setupRecurrencyAttempts() {
		this.recurrencyAttempts.forEach((recurrencyAttempt) => {
			const startDate = recurrencyAttempt.interval.start;
			const endDate = recurrencyAttempt.interval.end;
			const luxonSlotHour = startDate.set({
				hour: startDate.hour,
				minute: startDate.minute < 30 ? 0 : 30,
			});
			const duration = endDate.diff(startDate, 'minutes').minutes;

			this.hours.forEach((hour, hourIndex) => {
				const hasReccurrencyAttemptOverlay =
					this.recurrencyAttemptService.hasReccurrencyAttemptOverlay(
						this.recurrencyAttempts,
						hour.interval
					);
				if (
					hour.hour === luxonSlotHour.toFormat('HH:mm') &&
					recurrencyAttempt.luxonDate.hasSame(hour.luxonDate, 'day')
				) {
					this.hours[hourIndex].recurrencyAttempts.push({
						...recurrencyAttempt,
						duration,
					});
				}
				this.hours[hourIndex].hasRecurrencyOverlaping =
					hasReccurrencyAttemptOverlay;
			});
		});
	}

	private setupSlots(e?: ICalendarEvent) {
		const firstHourOfToday = 0;

		if (e && !this.eventsList.some((ev) => ev.id === e.id)) {
			this.eventsList.push(e);
		}

		this.eventsList = this.eventsList.filter((ev) => {
			return (
				ev.status !== 'cancelled' ||
				(e && ev.id === e.id && e.status !== 'cancelled')
			);
		});

		this.eventsList.forEach((event) => {
			const startHour = DateTime.fromJSDate(event.day).hour;
			const startMinute = DateTime.fromJSDate(event.day).minute;

			this.hours = this.hours.map((hour) => {
				if (startHour === hour.luxonDate.hour) {
					if (
						(startMinute >= 30 && hour.isHalfHour) ||
						(startMinute < 30 && !hour.isHalfHour)
					) {
						const duration = DateTime.fromISO(event.endTime)
							.diff(DateTime.fromISO(event.startTime), 'minutes')
							.toObject().minutes;
						return {
							...hour,
							events: [
								...hour.events,
								{
									calendarOffset: `${
										startMinute >= 0 && !hour.isHalfHour
											? startMinute
											: startMinute - 30
									}px`,
									calendarHeight: `${duration * this.cellProportion}px`,
									...event,
								},
							],
						};
					}
				}
				return hour;
			});
		});

		this.dayTotalEvents = this.eventsList.length;

		return firstHourOfToday;
	}

	private getBusySlots(): void {
		// Get first and last days of current month.
		// Get current day.
		const firstDay = this.activeDate.date;
		// Get events.
		this.availabilityService.getBusySlotsForDay(firstDay).then((result) => {
			const busySlotList: IBusySlot[] = result;
			busySlotList.forEach((busySlotEvent) => {
				const startHour = DateTime.fromJSDate(busySlotEvent.startDate).hour;
				const startMinute = DateTime.fromJSDate(busySlotEvent.startDate).minute;
				this.hours = this.hours.map((hour) => {
					if (startHour === hour.luxonDate.hour) {
						if (
							(startMinute >= 30 && hour.isHalfHour) ||
							(startMinute < 30 && !hour.isHalfHour)
						) {
							const duration = DateTime.fromJSDate(busySlotEvent.endDate)
								.diff(DateTime.fromJSDate(busySlotEvent.startDate), 'minutes')
								.toObject().minutes;
							return {
								...hour,
								busySlots: [
									...hour.busySlots,
									{
										calendarOffset: `${
											startMinute >= 0 && !hour.isHalfHour
												? startMinute
												: startMinute - 30
										}px`,
										calendarHeight: `${duration * this.cellProportion}px`,
										...busySlotEvent,
									},
								],
							};
						}
					}
					return hour;
				});
			});
		});
	}
}
